import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  theme,
  Table,
  Flex,
  Pagination,
} from '@xometry/xometry_loft';
import { Link as ReactLink } from 'react-router-dom';
import { css } from '@emotion/react';
import { useMutation } from '@apollo/client';
import { DeleteTaxExemptionQuery } from '../../../schema/generated/schema';

const taxExemption__link = css`
  text-decoration: none;
  color: ${theme.colors.blues[100]};
  display: block;
  margin: 12px 0 0 12px;

  :hover {
    text-decoration: underline;
  }
`;

const taxExemptionStatus__error = css`
  color: ${theme.colors.reds[100]};
`;

const taxExemptionStatus__pending = css`
  color: ${theme.colors.grays[1]};
`;

const taxExemptionStatus__active = css`
  color: ${theme.colors.greens[100]};
`;

/** Page Size for the Tax Exemptions Table */
const PAGE_SIZE = 6;

const TaxExemptions = ({ taxExemptions, isStaff, userID, profile }) => {
  const [selectedPage, setSelectedPage] = useState(1);
  const [deleteTaxExemption] = useMutation(DeleteTaxExemptionQuery);

  const rows = useMemo(
    () => (taxExemptions || []).filter(Boolean),
    [taxExemptions]
  );

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageSlice = rows.slice(
    (selectedPage - 1) * PAGE_SIZE,
    selectedPage * PAGE_SIZE
  );
  const canPreviousPage = selectedPage > 1;
  const canNextPage = selectedPage < pageCount;

  useEffect(() => {
    setSelectedPage(1);
  }, [profile?.id]);

  useEffect(() => {
    setSelectedPage((p) => Math.min(p, pageCount));
  }, [pageCount, rows.length]);

  const handleDeleteTaxExemption = (taxExemption) => {
    if (window.confirm('Do you wish to delete this tax exemption?')) {
      deleteTaxExemption({
        variables: {
          input: {
            taxExemptionId: taxExemption.id,
            billingAccountId: taxExemption.billingAccountId,
          },
        },
      })
        .then(() => window.location.reload())
        .catch((e) => {
          const errorMssgs = e.graphQLErrors.map(
            (errorObj) => errorObj.message
          );
          console.log(errorMssgs);
        });
    }
  };

  return (
    <div>
      {/* Display add tax exempt link if address is valid and in the US */}
      {profile.contact?.address?.country === 'United States' &&
        /^[\d-]+$/.test(profile.contact?.address?.zipcode) && (
          <Flex justifyContent="flex-end" margin={'8px 20px'}>
            <ReactLink
              to={{
                pathname: `/add-tax-exemption${isStaff ? `/${userID}` : ''}`,
              }}
              state={{ billingAccount: profile }} // Pass billing account id to tax exemption component
              css={taxExemption__link}
            >
              + Add Exemption
            </ReactLink>
          </Flex>
        )}
      <Table margin={'8px 20px 20px'} width="fill-available">
        <thead>
          <tr>
            <th>State</th>
            <th>Sign Date</th>
            <th>Expiration Date</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {pageSlice.map((exemption, index) => (
            <tr
              key={exemption.id ?? `${exemption.exposure_zone?.name}-${index}`}
            >
              <td>{exemption.exposure_zone?.name}</td>
              <td>{exemption.signed_date}</td>
              <td>{exemption.expiration_date}</td>
              <td
                css={css`
                  ${!exemption.actual_tax_code
                    ? taxExemptionStatus__pending
                    : /^EXPOSURE/.test(
                        exemption.actual_tax_code?.name.toUpperCase()
                      )
                    ? taxExemptionStatus__error
                    : taxExemptionStatus__active}
                `}
              >
                {/* If tax code is not present then display Pending status.
                  Otherwise display if cert is Expired or the actual tax code */}
                {!exemption.actual_tax_code
                  ? exemption.status.name
                  : exemption.actual_tax_code?.name.toUpperCase() ===
                    'EXPOSURE: EXPIRED CERT'
                  ? 'EXPIRED CERT'
                  : exemption.actual_tax_code?.name}
              </td>
              <td>
                <Button onClick={() => handleDeleteTaxExemption(exemption)}>
                  <i
                    className="far fa-trash-alt"
                    css={css`
                      margin-right: ${theme.space[1]};
                    `}
                  />
                  Remove
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {/* Inset matches Table margin (20px); extra right padding after controls. Order: < · numbers · > */}
      <Box
        width="100%"
        css={css`
          box-sizing: border-box;
          /* Align with Table horizontal margin (20px); extra space after last control */
          padding: 0 32px 20px 20px;
        `}
      >
        <Table.PaginationArea alignItems="center">
          <Box>
            Page {selectedPage} of {pageCount}
          </Box>
          <Flex
            alignItems="center"
            css={css`
              gap: ${theme.space[3]};
            `}
          >
            <Button
              onClick={() => setSelectedPage((p) => Math.max(1, p - 1))}
              disabled={!canPreviousPage}
            >
              {'<'}
            </Button>
            <Pagination
              numberOfPages={pageCount}
              selectedPage={selectedPage}
              setSelectedPageInParent={(pageNumber) => {
                setSelectedPage(pageNumber);
              }}
              navigationArrows={false}
            />
            <Button
              onClick={() => setSelectedPage((p) => Math.min(pageCount, p + 1))}
              disabled={!canNextPage}
            >
              {'>'}
            </Button>
          </Flex>
        </Table.PaginationArea>
      </Box>
      {!(
        profile.contact?.address?.country === 'United States' &&
        /^[\d-]+$/.test(profile.contact?.address?.zipcode)
      ) && (
        <div
          css={css`
            margin: 12px 0 0 12px;
          `}
        >
          Only Billing Profiles in the United States are able to use tax
          exemptions at this time. For international tax please{' '}
          <a
            href="https://support.xometry.com/hc/en-us/requests/new?ticket_form_id=360006164253"
            target="_blank"
            rel="noopener noreferrer"
            css={[
              taxExemption__link,
              css`
                display: inline;
                margin: 0;
              `,
            ]}
          >
            Contact Us
          </a>
          .
        </div>
      )}
    </div>
  );
};

export default TaxExemptions;
